import { useForm } from "react-hook-form";
import { useState, useEffect, useRef, useMemo } from "react";
import Modal from "./Modal";
import ModalRemove from "./ModalRemove";
import { useDispatch, useSelector } from "react-redux";
import {
  ObtenerTodos,
  Actualizar,
  Eliminar,
  Insertar,
} from "../../redux/formasSlice";
import WindowDimension from "../../Hooks/WindowDimension";
import Required from "../../Components/Required";
import { OrderTable } from "../../Components/OrderTable";
import useSubmit from "../../Hooks/useSubmit";
import { getAdminWaysToKnowPage } from "../../Api/content";
import { useLocale } from "../../i18n/localeContext";
import { Skeleton } from "antd";
import { StrapiError } from '../../Inicio/Pages/StrapiError';
import { _translateValue } from "../translation-gestor/translateValue";

import { useValidation } from "../../Hooks/useValidation";

const SCREEN_NAME = "PantallaFormas";
function Formas(props) {
  const { validation } = useValidation();
  const { locale } = useLocale();
  const [adminWaysToKnowPage, setAdminWaysToKnowPage] = useState(null);
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  const [translatedNombres, setTranslatedNombres] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [tituloModal, setTituloModal] = useState("Agregar");
  const [removeState, changeRemoveState] = useState(false);
  const [itemSelected, setItemSelected] = useState({});
  const { width } = WindowDimension();

  const dispatch = useDispatch();
  let busqueda = useRef();

  const { submitAction } = useSubmit();

  const { formas } = useSelector((state) => state.formas);

  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
  } = useForm();

  const columns = useMemo(() => {
    const table = adminWaysToKnowPage?.howKnow_table ?? [];
    return [
      { label: table[0]?.label || "Código", accessor: "formaConocernosId", sortable: true },
      { 
        label: table[1]?.label || "Nombre", 
        accessor: "formaNombre", 
        sortable: true,
        render: (item) => (
          <td>{translatedNombres[item.formaNombre] || item.formaNombre}</td>
        ),
      },
      { label: table[2]?.label || "Acción", accessor: "action", sortable: false },
    ];
  }, [adminWaysToKnowPage, translatedNombres]);

  const showModalEdit = (item) => {
    reset(item);
    setTituloModal(adminWaysToKnowPage?.subtitleT || "Editar");
    setShowModal(true);
  };

  const closeModalEdit = () => {
    reset({ formaConocernosId: 0, formaNombre: "" });
    setShowModal(false);
  };

  const showRemoveModalEdit = (item) => {
    setItemSelected(item);
    changeRemoveState(true);
  };

  const closeRemoveModalEdit = () => {
    setItemSelected({});
    changeRemoveState(false);
  };

  const cargarFormas = () => {
    dispatch(ObtenerTodos());
  };

  const cargarFormasPorCaracter = () => {
    dispatch(ObtenerTodos(busqueda.current.value));
  };

  const eliminar = async () => {
    submitAction(Eliminar, itemSelected.formaConocernosId, () => {
      closeRemoveModalEdit();
      cargarFormas();
    });
  };

  const onSubmit = async (data) => {
    submitAction(
      data.formaConocernosId > 0 ? Actualizar : Insertar,
      data,
      () => {
        closeModalEdit();
        cargarFormas();
      }
    );
  };

  useEffect(() => {
    cargarFormas();
  }, [dispatch]);


  useEffect(() => {
    let cancel = false;
    setIsLoadingContent(true);
    getAdminWaysToKnowPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          setAdminWaysToKnowPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => { 
        if (!cancel) {
          setAdminWaysToKnowPage(null);
          setIsLoadingContent(false);
        }
      });
    return () => { cancel = true };
  }, [locale])

  // Efecto para traducir el campo "Nombre" cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      // Verificar si hay datos para traducir
      const hasFormas = formas && Array.isArray(formas) && formas.length > 0;

      // Si no hay datos, no hacer nada
      if (!hasFormas) {
        setIsTranslating(false);
        return;
      }

      // Iniciar traducción
      setIsTranslating(true);

      const nombreTranslations = {};

      // Traducir solo el campo "Nombre"
      const translationPromises = formas.map(async (item) => {
        if (item.formaNombre) {
          const translated = await _translateValue(SCREEN_NAME, item.formaNombre, locale);
          nombreTranslations[item.formaNombre] = translated;
        }
      });
      await Promise.all(translationPromises);

      // Actualizar estados y finalizar traducción
      setTranslatedNombres(nombreTranslations);
      setIsTranslating(false);
    };

    translateData();
  }, [formas, locale]);

  if (isLoadingContent || isTranslating) {
    return (
      <div className="main-admin">
        <div className="main-admin-bar">
          <div className="header-admin-bar-items">
            <Skeleton.Button style={{ width: 40, height: 40 }} active />
            <Skeleton.Input style={{ width: 400, height: 30 }} active />
          </div>
          <div className="header-admin-bar-items ml-auto">
            <Skeleton.Input style={{ width: 200, height: 40 }} active />
          </div>
        </div>
        <div className="table-container">
          <Skeleton.Input style={{ width: '100%', height: 400, minWidth: '800px' }} active />
        </div>
      </div>
    );
  }

  return (
    <div className="main-admin">
      <div className="main-admin-bar">
        <div className="header-admin-bar-items">
          <button
            className="main-admin-button"
            onClick={() => {
              setShowModal(true);
              setTituloModal(adminWaysToKnowPage?.subtitleO || "Agregar");
              setValue("formaNombre", "");
              reset();
            }}
          >
            +
          </button>
          <h2>{adminWaysToKnowPage?.title || "Formas en que se enteró de CENFOTEC"}</h2>
        </div>
        <div className="header-admin-bar-items ml-auto">
          <input
            type="search"
            className="form-control"
            onInput={cargarFormasPorCaracter}
            placeholder={adminWaysToKnowPage?.search_input || "Búsqueda"}
            ref={busqueda}
          />
        </div>
      </div>

      <div className="table-container">
        {formas.length > 0 ? (
          <>
            <OrderTable
              {...{ columns }}
              data={formas}
              clase="table-adm"
              actions={[
                {
                  icon: "",
                  clase: "text-primary",
                  event: (item) => showModalEdit(item),
                },
                {
                  icon: "",
                  clase: "text-primary",
                  event: (item) => showRemoveModalEdit(item),
                },
              ]}
            />
          </>
        ) : (
          <div className="no-results-container">
            <img src={props.img} alt="Imagen sin resultados" />
            <h1>{props.noResultMessage}</h1>
          </div>
        )}
      </div>

      <Modal
        estado={showModal}
        cambiarEstado={closeModalEdit}
        titulo={tituloModal}
        clase={`${width < 576 ? "full-width" : "small"}`}
        buttonGroup={
          <>
            <button
              type="submit"
              className="button-text"
              onClick={handleSubmit(onSubmit)}
            >
              {adminWaysToKnowPage?.save_button || "Guardar"}
            </button>
          </>
        }
      >
        <form>
          <div className="form-group">
            <label>{adminWaysToKnowPage?.wayKnowtext || "Forma de conocer a CENFOTEC"}</label>
            <Required />
            <input
              className="form-control"
              name="formaNombre"
              {...register("formaNombre", { required: validation?.required_field || "Campo requerido" })}
            />
            {errors.formaNombre && (
              <p className="error">{errors.formaNombre.message}</p>
            )}
          </div>
        </form>
      </Modal>

      <ModalRemove
        estado={removeState}
        closeModalHandled={closeRemoveModalEdit}
        title={adminWaysToKnowPage?.subtitleTR || "Eliminar plantillla"}
        message={adminWaysToKnowPage?.question || "¿Seguro que desea eliminar el siguiente registro?"}
        item={itemSelected.formaNombre}
        btnGroup={
          <>
            <button className="button-text text-red" onClick={eliminar}>
              {adminWaysToKnowPage?.confirm_button || "Confirmar"}
            </button>
            <button
              className="button-text text-grey"
              onClick={() => {
                closeRemoveModalEdit(false);
              }}
            >
              {adminWaysToKnowPage?.cancel_button || "Cancelar"}
            </button>
          </>
        }
      />
    </div>
  );
}

export default Formas;
